/**
 * Admin Export Service
 * Service để export dữ liệu từ database theo các filter
 */

import { API_CONFIG } from '../../config/api.config';
import { User, UserProfile } from '../../types/auth';
import { StudyProgress } from '../../types/auth';
import { QuizResult } from '../../types/database';
import { GameResult } from '../../types/database';

export interface ExportFilters {
  lessonId?: string;
  quizId?: string;
  userId?: string;
  classId?: string;
  chapterId?: string;
  startDate?: Date;
  endDate?: Date;
  role?: 'student' | 'teacher' | 'admin';
  school?: string;
  grade?: string;
}

export interface ExportData {
  users: UserWithProfile[];
  studyProgress: StudyProgress[];
  quizResults: QuizResult[];
  gameResults: GameResult[];
  summary: {
    totalUsers: number;
    totalStudents: number;
    totalTeachers: number;
    totalStudyProgress: number;
    totalQuizResults: number;
    totalGameResults: number;
  };
}

export interface UserWithProfile extends User {
  profile?: UserProfile;
  school?: string;
  class?: string;
}

class AdminExportService {
  /**
   * Export dữ liệu theo filter
   */
  async exportData(filters: ExportFilters = {}): Promise<ExportData> {
    // Lấy dữ liệu THẬT từ backend Python (thay SQLite client-side cũ đã không còn dữ liệu).
    const params = new URLSearchParams();
    if (filters.role) params.set('role', filters.role);
    if (filters.userId) params.set('userId', filters.userId);
    if (filters.quizId) params.set('quizId', filters.quizId);
    if (filters.lessonId) params.set('lessonId', filters.lessonId);
    if (filters.startDate) params.set('startDate', filters.startDate.toISOString());
    if (filters.endDate) params.set('endDate', filters.endDate.toISOString());

    const token =
      localStorage.getItem('auth_token') ||
      localStorage.getItem('jwt_token') ||
      localStorage.getItem('token');

    const res = await fetch(`${API_CONFIG.python.apiUrl}/stats/admin-export?${params.toString()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      throw new Error(`Không tải được dữ liệu xuất (mã ${res.status})`);
    }
    const data = await res.json();

    const toDate = (s: any): Date | undefined => (s ? new Date(s) : undefined);

    // Đổi chuỗi ISO → Date để các hàm xuất CSV/JSON dùng được như cũ.
    const users = (data.users || []).map((u: any) => ({
      ...u,
      createdAt: u.createdAt ? new Date(u.createdAt) : new Date(0),
      lastLoginAt: toDate(u.lastLoginAt),
    }));
    const studyProgress = (data.studyProgress || []).map((p: any) => ({
      ...p,
      completedAt: toDate(p.completedAt),
      createdAt: toDate(p.createdAt),
    }));
    const quizResults = (data.quizResults || []).map((q: any) => ({
      ...q,
      completedAt: toDate(q.completedAt),
    }));
    const gameResults = (data.gameResults || []).map((g: any) => ({
      ...g,
      completedAt: toDate(g.completedAt),
    }));

    return {
      users: users as any,
      studyProgress: studyProgress as any,
      quizResults: quizResults as any,
      gameResults: gameResults as any,
      summary: data.summary || {
        totalUsers: users.length,
        totalStudents: users.filter((u: any) => u.role === 'student').length,
        totalTeachers: users.filter((u: any) => u.role === 'teacher').length,
        totalStudyProgress: studyProgress.length,
        totalQuizResults: quizResults.length,
        totalGameResults: gameResults.length,
      },
    };
  }

  /**
   * Export to CSV format
   */
  exportToCSV(data: ExportData, type: 'users' | 'progress' | 'quizzes' | 'games' | 'all'): string {
    switch (type) {
      case 'users':
        return this.exportUsersToCSV(data.users);
      case 'progress':
        return this.exportProgressToCSV(data.studyProgress);
      case 'quizzes':
        return this.exportQuizzesToCSV(data.quizResults);
      case 'games':
        return this.exportGamesToCSV(data.gameResults);
      case 'all':
        return this.exportAllToCSV(data);
      default:
        return '';
    }
  }

  private exportUsersToCSV(users: UserWithProfile[]): string {
    const headers = ['ID', 'Email', 'Tên', 'Vai trò', 'Trường', 'Lớp', 'Ngày tạo', 'Lần đăng nhập cuối'];
    const rows = users.map(user => [
      user.id,
      user.email || '',
      user.name,
      user.role,
      user.school || '',
      user.class || '',
      user.createdAt.toISOString(),
      user.lastLoginAt ? user.lastLoginAt.toISOString() : '',
    ]);
    
    return this.arrayToCSV([headers, ...rows]);
  }

  private exportProgressToCSV(progress: StudyProgress[]): string {
    const headers = ['ID', 'User ID', 'Lesson ID', 'Tiến độ (%)', 'Thời gian (giây)', 'Trạng thái', 'Ngày tạo'];
    const rows = progress.map(p => [
      p.id,
      p.userId,
      p.lessonId,
      p.progress.toString(),
      p.timeSpent.toString(),
      p.completedAt ? 'completed' : 'in_progress',
      p.createdAt ? new Date(p.createdAt).toISOString() : '',
    ]);
    
    return this.arrayToCSV([headers, ...rows]);
  }

  private exportQuizzesToCSV(quizzes: QuizResult[]): string {
    const headers = ['ID', 'User ID', 'Quiz ID', 'Lesson ID', 'Điểm', 'Tổng điểm', 'Phần trăm', 'Thời gian (giây)', 'Đã đạt', 'Ngày tạo'];
    const rows = quizzes.map(q => [
      q.id,
      q.userId,
      q.quizId,
      q.lessonId,
      q.score.toString(),
      q.maxScore.toString(),
      q.percentage.toString(),
      q.timeSpent.toString(),
      q.passed ? 'Có' : 'Không',
      q.completedAt ? q.completedAt.toISOString() : '',
    ]);
    
    return this.arrayToCSV([headers, ...rows]);
  }

  private exportGamesToCSV(games: GameResult[]): string {
    if (games.length === 0) return 'Chưa có dữ liệu game';
    
    const headers = ['ID', 'User ID', 'Game ID', 'Lesson ID', 'Điểm', 'Tổng điểm', 'Phần trăm', 'Thắng', 'Ngày tạo'];
    const rows = games.map(g => [
      g.id,
      g.userId,
      g.gameId,
      g.lessonId,
      g.score.toString(),
      g.maxScore.toString(),
      g.percentage.toString(),
      g.won ? 'Có' : 'Không',
      g.completedAt ? g.completedAt.toISOString() : '',
    ]);
    
    return this.arrayToCSV([headers, ...rows]);
  }

  private exportAllToCSV(data: ExportData): string {
    const sections = [
      '=== TỔNG QUAN ===',
      `Tổng người dùng: ${data.summary.totalUsers}`,
      `Tổng học sinh: ${data.summary.totalStudents}`,
      `Tổng giáo viên: ${data.summary.totalTeachers}`,
      `Tổng tiến độ học tập: ${data.summary.totalStudyProgress}`,
      `Tổng kết quả trắc nghiệm: ${data.summary.totalQuizResults}`,
      `Tổng kết quả game: ${data.summary.totalGameResults}`,
      '',
      '=== NGƯỜI DÙNG ===',
      this.exportUsersToCSV(data.users),
      '',
      '=== TIẾN ĐỘ HỌC TẬP ===',
      this.exportProgressToCSV(data.studyProgress),
      '',
      '=== KẾT QUẢ TRẮC NGHIỆM ===',
      this.exportQuizzesToCSV(data.quizResults),
      '',
      '=== KẾT QUẢ GAME ===',
      this.exportGamesToCSV(data.gameResults),
    ];
    
    return sections.join('\n');
  }

  private arrayToCSV(rows: any[][]): string {
    return rows.map(row => 
      row.map(cell => {
        const str = String(cell || '');
        // Escape quotes and wrap in quotes if contains comma, quote, or newline
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      }).join(',')
    ).join('\n');
  }

  /**
   * Download CSV file
   */
  downloadCSV(csv: string, filename: string): void {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
  }

  /**
   * Export to JSON
   */
  exportToJSON(data: ExportData): string {
    return JSON.stringify(data, null, 2);
  }

  /**
   * Download JSON file
   */
  downloadJSON(json: string, filename: string): void {
    const blob = new Blob([json], { type: 'application/json;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
  }

  // Helper methods to map database rows to types
  private mapRowToUser(row: any): User {
    return {
      id: row.id,
      email: row.email,
      name: row.name,
      avatar: row.avatar,
      provider: row.provider || 'google',
      providerId: row.providerId,
      role: row.role,
      permissions: row.permissions ? JSON.parse(row.permissions) : [],
      createdAt: new Date(row.createdAt),
      updatedAt: new Date(row.updatedAt),
      lastLoginAt: row.lastLoginAt ? new Date(row.lastLoginAt) : undefined,
      isActive: Boolean(row.isActive),
      isVerified: Boolean(row.isVerified),
      teacherInfo: row.teacherInfo ? JSON.parse(row.teacherInfo) : undefined,
      studentInfo: row.studentInfo ? JSON.parse(row.studentInfo) : undefined,
    };
  }

  private mapRowToStudyProgress(row: any): StudyProgress {
    return {
      id: row.id,
      userId: row.userId,
      lessonId: row.lessonId,
      lessonType: 'bai-hoc', // Default
      progress: row.progress || 0,
      timeSpent: row.timeSpent || 0,
      attempts: 1,
      lastAccessedAt: row.lastAccessedAt ? new Date(row.lastAccessedAt) : new Date(),
      completedAt: row.completedAt ? new Date(row.completedAt) : undefined,
      createdAt: new Date(row.createdAt),
      updatedAt: new Date(row.updatedAt || row.createdAt),
    };
  }

  private mapRowToQuizResult(row: any): QuizResult {
    const answers = row.answers ? JSON.parse(row.answers) : [];
    const score = row.score || 0;
    const totalQuestions = row.totalQuestions || 0;
    const percentage = totalQuestions > 0 ? (score / totalQuestions) * 100 : 0;
    
    return {
      id: row.id,
      userId: row.userId,
      quizId: row.quizId,
      lessonId: row.lessonId || '',
      score,
      maxScore: totalQuestions,
      percentage,
      timeSpent: row.timeSpent || 0,
      answers,
      startedAt: new Date(row.createdAt),
      completedAt: new Date(row.createdAt),
      passed: row.passed === 1 || percentage >= 70,
      attempts: row.attemptNumber || 1,
    };
  }
}

export const adminExportService = new AdminExportService();

